import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import { grantLeaveBalances, grantLeaveDays } from "../../api/leave";
import {
  deletePermitHourGrant,
  grantPermitHours,
  listPermitHourGrants,
  updatePermitHourGrant,
} from "../../api/permit";
import {
  deleteMonthlyRestQuota,
  grantMonthlyRestQuota,
  listMonthlyRestQuotas,
  updateMonthlyRestQuota,
} from "../../api/timesheet";
import "./HrLeaveGrant.css";

function formatGrantDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("ar-EG");
}

const GRANT_MODES = [
  { value: "DAYS", label: "منح أيام إجازة" },
  { value: "BALANCE", label: "إضافة إلى الرصيد" },
  { value: "PERMIT_HOURS", label: "منح ساعات إذن" },
  { value: "REST_DAYS", label: "تخصيص راحات شهرية" },
];

const TARGET_OPTIONS = [
  { value: "ALL", label: "كل العاملين بالشركة" },
  { value: "DEPARTMENT", label: "إدارة معينة" },
  { value: "EMPLOYEES", label: "موظفين محددين" },
];

const LEAVE_TYPE_OPTIONS = [
  { value: "ANNUAL", label: "اعتيادية (مدفوعة)" },
  { value: "CASUAL", label: "عارضة (مدفوعة)" },
  { value: "SICK", label: "مرضية (مدفوعة)" },
];

const PERMIT_CHARGE_OPTIONS = [
  { value: "FREE", label: "مجاني (بدون خصم من الراتب)" },
  { value: "EMPLOYEE_PAID", label: "على حساب الموظف (خصم من الراتب)" },
];

const OCCASION_PRESETS = [
  "إجازة عيد",
  "إجازة زواج",
  "إجازة مولود",
  "إجازة رسمية",
];

function todayIso() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function currentYearMonth() {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function calendarDaysInclusive(start, end) {
  if (!start || !end) return 0;
  const from = new Date(`${start}T00:00:00`);
  const to = new Date(`${end}T00:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to < from) {
    return 0;
  }
  return Math.round((to - from) / (1000 * 60 * 60 * 24)) + 1;
}

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function leaveTypeLabel(value) {
  return LEAVE_TYPE_OPTIONS.find((o) => o.value === value)?.label || value;
}

function chargeTypeLabel(value) {
  return PERMIT_CHARGE_OPTIONS.find((o) => o.value === value)?.label || value;
}

function targetSummary(target, departmentName, selectedCount) {
  if (target === "ALL") return "كل العاملين بالشركة";
  if (target === "DEPARTMENT") return `إدارة: ${departmentName || "—"}`;
  return `${selectedCount} موظف محدد`;
}

function modeSubtitle(mode) {
  if (mode === "DAYS") {
    return "تسجيل إجازة معتمدة لأيام محددة (عيد، زواج، مولود، رسمية) للشركة أو قسم أو موظفين — بدون زيادة أو خصم من الرصيد، وبدون جزاءات بصمة";
  }
  if (mode === "BALANCE") {
    return "إضافة أيام لرصيد الإجازة المدفوعة لكل العاملين، أو إدارة معينة، أو موظفين محددين";
  }
  if (mode === "PERMIT_HOURS") {
    return "منح ساعات إذن إضافية لشهر محدد — مجانية أو على حساب الموظف — تزيد رصيد الأذونات الشهري فوق الحد الافتراضي";
  }
  return "تخصيص عدد أيام الراحات المسموح تسجيلها في التايم شيت لشهر محدد (الافتراضي 4 أيام)";
}

export default function HrLeaveGrant() {
  const { employeesByBranch, departments, refreshData, addRecentActivity } =
    useData();

  const initialYm = currentYearMonth();
  const [mode, setMode] = useState("DAYS");
  const [target, setTarget] = useState("ALL");
  const [departmentId, setDepartmentId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [leaveType, setLeaveType] = useState("ANNUAL");
  const [days, setDays] = useState("1");
  const [startDate, setStartDate] = useState(todayIso);
  const [endDate, setEndDate] = useState(todayIso);
  const [year, setYear] = useState(String(initialYm.year));
  const [month, setMonth] = useState(String(initialYm.month));
  const [permitHours, setPermitHours] = useState("2");
  const [chargeType, setChargeType] = useState("FREE");
  const [restDays, setRestDays] = useState("4");
  const [reason, setReason] = useState("");
  const [empSearch, setEmpSearch] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [grants, setGrants] = useState([]);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [listYear, setListYear] = useState(String(initialYm.year));
  const [listMonth, setListMonth] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [grantBusyId, setGrantBusyId] = useState(null);

  const showGrantsList = mode === "PERMIT_HOURS" || mode === "REST_DAYS";

  const loadGrants = useCallback(async () => {
    if (mode !== "PERMIT_HOURS" && mode !== "REST_DAYS") return;
    setGrantsLoading(true);
    try {
      const params = {};
      const y = Number(listYear);
      const m = Number(listMonth);
      if (Number.isInteger(y) && y >= 2020) params.year = y;
      if (Number.isInteger(m) && m >= 1 && m <= 12) params.month = m;
      const rows =
        mode === "PERMIT_HOURS"
          ? await listPermitHourGrants(params)
          : await listMonthlyRestQuotas(params);
      setGrants(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setGrants([]);
      setError(
        getApiErrorMessage(
          err,
          mode === "PERMIT_HOURS"
            ? "تعذر جلب منح ساعات الإذن"
            : "تعذر جلب تخصيصات الراحات",
        ),
      );
    } finally {
      setGrantsLoading(false);
    }
  }, [mode, listYear, listMonth]);

  useEffect(() => {
    if (!showGrantsList) {
      setGrants([]);
      setEditingId(null);
      setEditDraft(null);
      return;
    }
    loadGrants();
  }, [showGrantsList, loadGrants]);

  const allEmployees = useMemo(
    () =>
      Object.values(employeesByBranch || {})
        .flat()
        .filter((e) => e.status !== "terminated"),
    [employeesByBranch],
  );

  const activeDepartments = useMemo(
    () =>
      (departments || []).filter(
        (d) => d.isActive !== false && (d.backendId || d.id),
      ),
    [departments],
  );

  const filteredEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    if (!q) return allEmployees;
    return allEmployees.filter(
      (e) =>
        e.name?.toLowerCase().includes(q) ||
        e.code?.toLowerCase().includes(q) ||
        e.jobTitle?.toLowerCase().includes(q),
    );
  }, [allEmployees, empSearch]);

  const selectedDepartment = activeDepartments.find(
    (d) => String(d.backendId || d.id) === String(departmentId),
  );

  const previewCount = useMemo(() => {
    if (target === "ALL") return allEmployees.length;
    if (target === "DEPARTMENT") {
      if (!departmentId) return 0;
      return allEmployees.filter(
        (e) =>
          String(e.departmentBackendId || e.departmentId) ===
          String(departmentId),
      ).length;
    }
    return selectedIds.length;
  }, [target, allEmployees, departmentId, selectedIds]);

  const rangeDays = calendarDaysInclusive(startDate, endDate);

  function resetForm() {
    const ym = currentYearMonth();
    setTarget("ALL");
    setDepartmentId("");
    setSelectedIds([]);
    setLeaveType("ANNUAL");
    setDays("1");
    setStartDate(todayIso());
    setEndDate(todayIso());
    setYear(String(ym.year));
    setMonth(String(ym.month));
    setPermitHours("2");
    setChargeType("FREE");
    setRestDays("4");
    setReason("");
    setEmpSearch("");
  }

  function toggleEmployee(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function selectAllFiltered() {
    const ids = filteredEmployees.map((e) => e.id);
    setSelectedIds((prev) => [...new Set([...prev, ...ids])]);
  }

  function clearSelection() {
    setSelectedIds([]);
  }

  function validateTarget() {
    if (target === "DEPARTMENT" && !departmentId) {
      setError("اختر الإدارة");
      return false;
    }
    if (target === "EMPLOYEES" && !selectedIds.length) {
      setError("اختر موظفًا واحدًا على الأقل");
      return false;
    }
    if (previewCount < 1) {
      setError("لا يوجد موظفون مطابقون للاستهداف");
      return false;
    }
    return true;
  }

  function buildTargetPayload() {
    const payload = { target };
    if (target === "DEPARTMENT") payload.departmentId = departmentId;
    if (target === "EMPLOYEES") payload.employeeIds = selectedIds;
    return payload;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!validateTarget()) return;

    if (mode === "DAYS") {
      if (!startDate || !endDate) {
        setError("حدد تاريخ البداية والنهاية");
        return;
      }
      if (rangeDays < 1 || rangeDays > 30) {
        setError("مدة الإجازة يجب أن تكون بين يوم و 30 يومًا");
        return;
      }
      if (reason.trim().length < 3) {
        setError("اكتب سبب الإجازة (مثل إجازة عيد أو زواج)");
        return;
      }
    } else if (mode === "BALANCE") {
      const daysNum = Number(days);
      if (!Number.isInteger(daysNum) || daysNum < 1 || daysNum > 90) {
        setError("عدد الأيام يجب أن يكون رقمًا صحيحًا بين 1 و 90");
        return;
      }
    } else if (mode === "PERMIT_HOURS") {
      const yearNum = Number(year);
      const monthNum = Number(month);
      const hoursNum = Number(permitHours);
      if (!Number.isInteger(yearNum) || yearNum < 2020 || yearNum > 2100) {
        setError("السنة غير صالحة");
        return;
      }
      if (!Number.isInteger(monthNum) || monthNum < 1 || monthNum > 12) {
        setError("الشهر يجب أن يكون بين 1 و 12");
        return;
      }
      if (
        !Number.isFinite(hoursNum) ||
        hoursNum < 0.5 ||
        hoursNum > 40 ||
        Math.abs(hoursNum * 2 - Math.round(hoursNum * 2)) > 1e-9
      ) {
        setError("عدد الساعات يجب أن يكون من 0.5 إلى 40 بنصف ساعة");
        return;
      }
    } else if (mode === "REST_DAYS") {
      const yearNum = Number(year);
      const monthNum = Number(month);
      const restNum = Number(restDays);
      if (!Number.isInteger(yearNum) || yearNum < 2020 || yearNum > 2100) {
        setError("السنة غير صالحة");
        return;
      }
      if (!Number.isInteger(monthNum) || monthNum < 1 || monthNum > 12) {
        setError("الشهر يجب أن يكون بين 1 و 12");
        return;
      }
      if (!Number.isInteger(restNum) || restNum < 0 || restNum > 16) {
        setError("عدد أيام الراحات يجب أن يكون بين 0 و 16");
        return;
      }
    }

    const summary = targetSummary(
      target,
      selectedDepartment?.name,
      selectedIds.length,
    );

    setSubmitting(true);
    try {
      if (mode === "DAYS") {
        const result = await grantLeaveDays({
          ...buildTargetPayload(),
          startDate,
          endDate,
          reason: reason.trim(),
        });
        const granted = result?.grantedCount ?? previewCount;
        const skipped = result?.skippedCount ?? 0;
        const skipNote = skipped
          ? ` — تم تخطي ${skipped} لوجود إجازة متداخلة أو بدون حساب`
          : "";
        setSuccess(
          `تم منح إجازة مدفوعة من ${startDate} إلى ${endDate} (${rangeDays} يوم) لـ ${granted} موظف (${summary})${skipNote}. الرصيد لم يتغير، والبصمة لن تحتسب جزاءات على هذه الأيام.`,
        );
        addRecentActivity?.({
          type: "leave",
          message: `منح إجازة مدفوعة ${rangeDays} يوم لـ ${granted} موظف`,
        });
      } else if (mode === "BALANCE") {
        const daysNum = Number(days);
        const result = await grantLeaveBalances({
          ...buildTargetPayload(),
          leaveType,
          days: daysNum,
          reason: reason.trim() || undefined,
        });
        const granted = result?.grantedCount ?? previewCount;
        setSuccess(
          `تم إضافة ${daysNum} يوم إلى رصيد إجازة ${leaveTypeLabel(leaveType)} لـ ${granted} موظف (${summary})`,
        );
        addRecentActivity?.({
          type: "leave",
          message: `إضافة ${daysNum} يوم لرصيد الإجازة لـ ${granted} موظف`,
        });
      } else if (mode === "PERMIT_HOURS") {
        const hoursNum = Number(permitHours);
        const result = await grantPermitHours({
          ...buildTargetPayload(),
          year: Number(year),
          month: Number(month),
          hours: hoursNum,
          chargeType,
          reason: reason.trim() || undefined,
        });
        const granted = result?.grantedCount ?? previewCount;
        setSuccess(
          `تم منح ${hoursNum} ساعة إذن (${chargeTypeLabel(chargeType)}) لشهر ${month}/${year} لـ ${granted} موظف (${summary})`,
        );
        addRecentActivity?.({
          type: "permit",
          message: `منح ${hoursNum} ساعة إذن لـ ${granted} موظف`,
        });
      } else {
        const restNum = Number(restDays);
        const result = await grantMonthlyRestQuota({
          ...buildTargetPayload(),
          year: Number(year),
          month: Number(month),
          allottedDays: restNum,
          reason: reason.trim() || undefined,
        });
        const granted = result?.grantedCount ?? previewCount;
        setSuccess(
          `تم تخصيص ${restNum} يوم راحة لشهر ${month}/${year} لـ ${granted} موظف (${summary})`,
        );
        addRecentActivity?.({
          type: "timesheet",
          message: `تخصيص ${restNum} راحة شهرية لـ ${granted} موظف`,
        });
      }
      resetForm();
      await refreshData?.();
      if (mode === "PERMIT_HOURS" || mode === "REST_DAYS") {
        await loadGrants();
      }
    } catch (err) {
      const fallbacks = {
        DAYS: "تعذر منح الإجازة",
        BALANCE: "تعذر منح أيام الإجازة",
        PERMIT_HOURS: "تعذر منح ساعات الإذن",
        REST_DAYS: "تعذر تخصيص أيام الراحات",
      };
      setError(getApiErrorMessage(err, fallbacks[mode] || "تعذر تنفيذ العملية"));
    } finally {
      setSubmitting(false);
    }
  }

  function startEditGrant(row) {
    setEditingId(row.id);
    if (mode === "PERMIT_HOURS") {
      setEditDraft({
        year: String(row.year),
        month: String(row.month),
        hours: String(row.hours),
        chargeType: row.chargeType,
        reason: row.reason || "",
      });
    } else {
      setEditDraft({
        year: String(row.year),
        month: String(row.month),
        allottedDays: String(row.allottedDays),
        reason: row.reason || "",
      });
    }
  }

  function cancelEditGrant() {
    setEditingId(null);
    setEditDraft(null);
  }

  async function saveEditGrant(id) {
    if (!editDraft) return;
    setGrantBusyId(id);
    setError("");
    setSuccess("");
    try {
      if (mode === "PERMIT_HOURS") {
        const hoursNum = Number(editDraft.hours);
        if (
          !Number.isFinite(hoursNum) ||
          hoursNum < 0.5 ||
          hoursNum > 40 ||
          Math.abs(hoursNum * 2 - Math.round(hoursNum * 2)) > 1e-9
        ) {
          setError("عدد الساعات يجب أن يكون من 0.5 إلى 40 بنصف ساعة");
          return;
        }
        await updatePermitHourGrant(id, {
          year: Number(editDraft.year),
          month: Number(editDraft.month),
          hours: hoursNum,
          chargeType: editDraft.chargeType,
          reason: editDraft.reason.trim() || null,
        });
        setSuccess("تم تعديل منحة ساعات الإذن");
      } else {
        const daysNum = Number(editDraft.allottedDays);
        if (!Number.isInteger(daysNum) || daysNum < 0 || daysNum > 16) {
          setError("عدد أيام الراحات يجب أن يكون بين 0 و 16");
          return;
        }
        await updateMonthlyRestQuota(id, {
          year: Number(editDraft.year),
          month: Number(editDraft.month),
          allottedDays: daysNum,
          reason: editDraft.reason.trim() || null,
        });
        setSuccess("تم تعديل تخصيص الراحات الشهرية");
      }
      cancelEditGrant();
      await loadGrants();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر حفظ التعديل"));
    } finally {
      setGrantBusyId(null);
    }
  }

  async function removeGrant(id) {
    const ok = window.confirm(
      mode === "PERMIT_HOURS"
        ? "حذف منحة ساعات الإذن هذه؟"
        : "حذف تخصيص الراحات؟ سيعود الحد الافتراضي (4 أيام).",
    );
    if (!ok) return;
    setGrantBusyId(id);
    setError("");
    setSuccess("");
    try {
      if (mode === "PERMIT_HOURS") {
        await deletePermitHourGrant(id);
        setSuccess("تم حذف منحة ساعات الإذن");
      } else {
        await deleteMonthlyRestQuota(id);
        setSuccess("تم حذف تخصيص الراحات");
      }
      if (editingId === id) cancelEditGrant();
      await loadGrants();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر الحذف"));
    } finally {
      setGrantBusyId(null);
    }
  }

  const submitLabel =
    mode === "DAYS"
      ? "منح الإجازة"
      : mode === "BALANCE"
        ? "إضافة إلى الرصيد"
        : mode === "PERMIT_HOURS"
          ? "منح ساعات الإذن"
          : "تخصيص الراحات";

  return (
    <div className="hr-leave-grant" dir="rtl">
      <div className="hr-leave-grant-header">
        <div>
          <h1 className="hr-leave-grant-title">منح إجازات وأذونات وراحات</h1>
          <p className="hr-leave-grant-subtitle">{modeSubtitle(mode)}</p>
        </div>
      </div>

      <div className="hr-leave-grant-modes" role="tablist">
        {GRANT_MODES.map((opt) => (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={mode === opt.value}
            className={`hr-leave-grant-mode${mode === opt.value ? " active" : ""}`}
            onClick={() => {
              setMode(opt.value);
              setError("");
              setSuccess("");
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {error ? <div className="hr-leave-grant-alert error">{error}</div> : null}
      {success ? (
        <div className="hr-leave-grant-alert success">{success}</div>
      ) : null}

      <form className="hr-leave-grant-form" onSubmit={handleSubmit}>
        <h2 className="hr-leave-grant-section-title">تفاصيل المنح</h2>

        <div className="hr-leave-grant-field">
          <span>الاستهداف</span>
          <div className="hr-leave-grant-target-row">
            {TARGET_OPTIONS.map((opt) => (
              <label key={opt.value} className="hr-leave-grant-radio">
                <input
                  type="radio"
                  name="leave-grant-target"
                  value={opt.value}
                  checked={target === opt.value}
                  onChange={() => {
                    setTarget(opt.value);
                    setSelectedIds([]);
                    setError("");
                  }}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {target === "DEPARTMENT" ? (
          <label className="hr-leave-grant-field">
            <span>الإدارة</span>
            <select
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              required
            >
              <option value="">اختر الإدارة</option>
              {activeDepartments.map((d) => (
                <option key={d.backendId || d.id} value={d.backendId || d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {target === "EMPLOYEES" ? (
          <div className="hr-leave-grant-employees">
            <div className="hr-leave-grant-employees-toolbar">
              <input
                type="search"
                className="hr-leave-grant-search"
                value={empSearch}
                onChange={(e) => setEmpSearch(e.target.value)}
                placeholder="بحث بالاسم أو الكود..."
              />
              <div className="hr-leave-grant-employees-actions">
                <button type="button" onClick={selectAllFiltered}>
                  تحديد الظاهر
                </button>
                <button type="button" onClick={clearSelection}>
                  مسح التحديد
                </button>
                <span className="hr-leave-grant-selected-count">
                  {selectedIds.length} مختار
                </span>
              </div>
            </div>
            <div className="hr-leave-grant-employee-list">
              {filteredEmployees.length === 0 ? (
                <div className="hr-leave-grant-empty-inline">لا يوجد موظفون</div>
              ) : (
                filteredEmployees.map((emp) => (
                  <label key={emp.id} className="hr-leave-grant-employee-row">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(emp.id)}
                      onChange={() => toggleEmployee(emp.id)}
                    />
                    <span className="hr-leave-grant-employee-name">
                      {emp.name}
                    </span>
                    <span className="hr-leave-grant-employee-meta">
                      {emp.code} · {emp.jobTitle}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
        ) : null}

        {mode === "DAYS" ? (
          <>
            <div className="hr-leave-grant-row">
              <label className="hr-leave-grant-field">
                <span>من تاريخ</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    if (endDate && e.target.value > endDate) {
                      setEndDate(e.target.value);
                    }
                  }}
                  required
                />
              </label>
              <label className="hr-leave-grant-field">
                <span>إلى تاريخ</span>
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </label>
            </div>

            <div className="hr-leave-grant-field">
              <span>المناسبة / السبب</span>
              <div className="hr-leave-grant-presets">
                {OCCASION_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className={`hr-leave-grant-preset${reason === preset ? " active" : ""}`}
                    onClick={() => setReason(preset)}
                  >
                    {preset}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                required
                placeholder="مثال: إجازة عيد الفطر — يظهر للموظف وفي سجل الإجازة"
              />
            </div>
          </>
        ) : null}

        {mode === "BALANCE" ? (
          <>
            <div className="hr-leave-grant-row">
              <label className="hr-leave-grant-field">
                <span>نوع الإجازة المدفوعة</span>
                <select
                  value={leaveType}
                  onChange={(e) => setLeaveType(e.target.value)}
                >
                  {LEAVE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="hr-leave-grant-field">
                <span>عدد الأيام المضافة للرصيد</span>
                <input
                  type="number"
                  min={1}
                  max={90}
                  step={1}
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  required
                />
              </label>
            </div>

            <label className="hr-leave-grant-field">
              <span>السبب (اختياري — يظهر في إشعار الموظف)</span>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                placeholder="مثال: منحة رصيد إضافية"
              />
            </label>
          </>
        ) : null}

        {mode === "PERMIT_HOURS" ? (
          <>
            <div className="hr-leave-grant-row">
              <label className="hr-leave-grant-field">
                <span>السنة</span>
                <input
                  type="number"
                  min={2020}
                  max={2100}
                  step={1}
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  required
                />
              </label>
              <label className="hr-leave-grant-field">
                <span>الشهر</span>
                <input
                  type="number"
                  min={1}
                  max={12}
                  step={1}
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  required
                />
              </label>
              <label className="hr-leave-grant-field">
                <span>عدد الساعات</span>
                <input
                  type="number"
                  min={0.5}
                  max={40}
                  step={0.5}
                  value={permitHours}
                  onChange={(e) => setPermitHours(e.target.value)}
                  required
                />
              </label>
            </div>

            <div className="hr-leave-grant-field">
              <span>نوع الخصم</span>
              <div className="hr-leave-grant-target-row">
                {PERMIT_CHARGE_OPTIONS.map((opt) => (
                  <label key={opt.value} className="hr-leave-grant-radio">
                    <input
                      type="radio"
                      name="permit-charge-type"
                      value={opt.value}
                      checked={chargeType === opt.value}
                      onChange={() => setChargeType(opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            <label className="hr-leave-grant-field">
              <span>السبب (اختياري)</span>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                placeholder="مثال: منحة ساعات إذن لظرف خاص"
              />
            </label>
          </>
        ) : null}

        {mode === "REST_DAYS" ? (
          <>
            <div className="hr-leave-grant-row">
              <label className="hr-leave-grant-field">
                <span>السنة</span>
                <input
                  type="number"
                  min={2020}
                  max={2100}
                  step={1}
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  required
                />
              </label>
              <label className="hr-leave-grant-field">
                <span>الشهر</span>
                <input
                  type="number"
                  min={1}
                  max={12}
                  step={1}
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  required
                />
              </label>
              <label className="hr-leave-grant-field">
                <span>عدد أيام الراحات في الشهر</span>
                <input
                  type="number"
                  min={0}
                  max={16}
                  step={1}
                  value={restDays}
                  onChange={(e) => setRestDays(e.target.value)}
                  required
                />
              </label>
            </div>

            <label className="hr-leave-grant-field">
              <span>السبب (اختياري)</span>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                placeholder="مثال: راحات إضافية لهذا الشهر"
              />
            </label>
          </>
        ) : null}

        <div className="hr-leave-grant-preview">
          {mode === "DAYS" ? (
            <>
              سيتم تسجيل إجازة مدفوعة معتمدة لمدة <strong>{rangeDays || "—"}</strong>{" "}
              يوم لـ <strong>{previewCount}</strong> موظف — بدون خصم من الرصيد
            </>
          ) : null}
          {mode === "BALANCE" ? (
            <>
              سيتم إضافة الأيام إلى الرصيد لـ <strong>{previewCount}</strong> موظف
            </>
          ) : null}
          {mode === "PERMIT_HOURS" ? (
            <>
              سيتم منح <strong>{permitHours || "—"}</strong> ساعة إذن (
              {chargeTypeLabel(chargeType)}) لشهر{" "}
              <strong>
                {month}/{year}
              </strong>{" "}
              لـ <strong>{previewCount}</strong> موظف
            </>
          ) : null}
          {mode === "REST_DAYS" ? (
            <>
              سيتم تخصيص <strong>{restDays || "—"}</strong> يوم راحة لشهر{" "}
              <strong>
                {month}/{year}
              </strong>{" "}
              لـ <strong>{previewCount}</strong> موظف في التايم شيت
            </>
          ) : null}
        </div>

        <button
          type="submit"
          className="hr-leave-grant-submit"
          disabled={submitting || previewCount < 1}
        >
          {submitting ? "جاري المنح..." : submitLabel}
        </button>
      </form>

      {showGrantsList ? (
        <section className="hr-leave-grant-history">
          <div className="hr-leave-grant-history-header">
            <h2 className="hr-leave-grant-section-title">
              {mode === "PERMIT_HOURS"
                ? "المنح المسجّلة لساعات الإذن"
                : "تخصيصات الراحات المسجّلة"}
            </h2>
            <div className="hr-leave-grant-history-filters">
              <label>
                السنة
                <input
                  type="number"
                  min={2020}
                  max={2100}
                  value={listYear}
                  onChange={(e) => setListYear(e.target.value)}
                />
              </label>
              <label>
                الشهر
                <select
                  value={listMonth}
                  onChange={(e) => setListMonth(e.target.value)}
                >
                  <option value="">كل الشهور</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={String(m)}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="hr-leave-grant-history-refresh"
                onClick={loadGrants}
                disabled={grantsLoading}
              >
                {grantsLoading ? "جاري التحميل..." : "تحديث"}
              </button>
            </div>
          </div>

          {grantsLoading && !grants.length ? (
            <div className="hr-leave-grant-empty-inline">جاري التحميل...</div>
          ) : null}
          {!grantsLoading && !grants.length ? (
            <div className="hr-leave-grant-empty-inline">
              لا توجد منح مسجّلة لهذا الفلتر
            </div>
          ) : null}

          {grants.length > 0 ? (
            <div className="hr-leave-grant-table-wrap">
              <table className="hr-leave-grant-table">
                <thead>
                  <tr>
                    <th>الموظف</th>
                    <th>الشهر</th>
                    <th>
                      {mode === "PERMIT_HOURS" ? "الساعات / النوع" : "أيام الراحة"}
                    </th>
                    <th>السبب</th>
                    <th>بواسطة</th>
                    <th>التاريخ</th>
                    <th>إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {grants.map((row) => {
                    const isEditing = editingId === row.id;
                    const busy = grantBusyId === row.id;
                    return (
                      <tr key={row.id}>
                        <td>
                          <div className="hr-leave-grant-employee-name">
                            {row.employeeName}
                          </div>
                          <div className="hr-leave-grant-employee-meta">
                            {row.employeeCode}
                            {row.departmentName
                              ? ` · ${row.departmentName}`
                              : ""}
                          </div>
                        </td>
                        <td>
                          {isEditing ? (
                            <div className="hr-leave-grant-edit-ym">
                              <input
                                type="number"
                                min={1}
                                max={12}
                                value={editDraft?.month || ""}
                                onChange={(e) =>
                                  setEditDraft((d) => ({
                                    ...d,
                                    month: e.target.value,
                                  }))
                                }
                              />
                              /
                              <input
                                type="number"
                                min={2020}
                                max={2100}
                                value={editDraft?.year || ""}
                                onChange={(e) =>
                                  setEditDraft((d) => ({
                                    ...d,
                                    year: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          ) : (
                            `${row.month}/${row.year}`
                          )}
                        </td>
                        <td>
                          {isEditing && mode === "PERMIT_HOURS" ? (
                            <div className="hr-leave-grant-edit-stack">
                              <input
                                type="number"
                                min={0.5}
                                max={40}
                                step={0.5}
                                value={editDraft?.hours || ""}
                                onChange={(e) =>
                                  setEditDraft((d) => ({
                                    ...d,
                                    hours: e.target.value,
                                  }))
                                }
                              />
                              <select
                                value={editDraft?.chargeType || "FREE"}
                                onChange={(e) =>
                                  setEditDraft((d) => ({
                                    ...d,
                                    chargeType: e.target.value,
                                  }))
                                }
                              >
                                {PERMIT_CHARGE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ) : null}
                          {isEditing && mode === "REST_DAYS" ? (
                            <input
                              type="number"
                              min={0}
                              max={16}
                              step={1}
                              value={editDraft?.allottedDays || ""}
                              onChange={(e) =>
                                setEditDraft((d) => ({
                                  ...d,
                                  allottedDays: e.target.value,
                                }))
                              }
                            />
                          ) : null}
                          {!isEditing && mode === "PERMIT_HOURS" ? (
                            <>
                              <strong>{row.hours}</strong> ساعة —{" "}
                              {row.chargeType === "FREE"
                                ? "مجاني"
                                : "على حساب الموظف"}
                            </>
                          ) : null}
                          {!isEditing && mode === "REST_DAYS" ? (
                            <>
                              <strong>{row.allottedDays}</strong> يوم
                            </>
                          ) : null}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="text"
                              maxLength={500}
                              value={editDraft?.reason || ""}
                              onChange={(e) =>
                                setEditDraft((d) => ({
                                  ...d,
                                  reason: e.target.value,
                                }))
                              }
                            />
                          ) : (
                            row.reason || "—"
                          )}
                        </td>
                        <td>{row.createdByName || "—"}</td>
                        <td>
                          {formatGrantDate(row.updatedAt || row.createdAt)}
                        </td>
                        <td>
                          <div className="hr-leave-grant-row-actions">
                            {isEditing ? (
                              <>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => saveEditGrant(row.id)}
                                >
                                  حفظ
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={cancelEditGrant}
                                >
                                  إلغاء
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  disabled={busy || Boolean(editingId)}
                                  onClick={() => startEditGrant(row)}
                                >
                                  تعديل
                                </button>
                                <button
                                  type="button"
                                  className="danger"
                                  disabled={busy || Boolean(editingId)}
                                  onClick={() => removeGrant(row.id)}
                                >
                                  حذف
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
